import { Database } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@/lib/auth';
import { isBackendConfigured } from '@/lib/env';
import { ButtonLink, EmptyState, Spinner } from './ui';

export function BackendNotConfigured() {
  return (
    <EmptyState
      icon={<Database className="size-10" />}
      title="Backend not configured"
      action={<ButtonLink to="/tools">Open clinical tools</ButtonLink>}
    >
      Run <code>npx neon deploy</code> in the project root so <code>.env.local</code> contains <code>NEON_AUTH_BASE_URL</code> and{' '}
      <code>NEON_DATA_API_URL</code>, then restart the dev server. Clinical tools and the reference formulary work without a backend.
    </EmptyState>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { ready, user } = useAuth();
  const location = useLocation();
  if (!isBackendConfigured) return <BackendNotConfigured />;
  if (!ready) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

/** Educators, medical directors, admins and owners of the active org, or platform admins. */
export function RequireStaff({ children }: { children: ReactNode }) {
  const { isEditor, isPlatformAdmin, memberships, profile } = useAuth();
  if (!profile) return <Spinner />;
  if (!isEditor && !isPlatformAdmin && !memberships.some((m) => m.role !== 'provider')) return <Navigate to="/" replace />;
  return <>{children}</>;
}
